import {
  AgxBadge,
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  Icon_Chevron,
  SelectChipOption,
} from '@urbanx/agx.ui-components2';
import { useCallback, useMemo, useRef } from 'react';
import { TaskCell } from 'pages/allTasks/Cells/TaskCell';
import useTaskSearch from 'pages/allTasks/useTaskSearch';
import TaskAgencyCell from 'pages/allTasks/Cells/TaskAgencyCell';
import TaskCampaignCell from 'pages/allTasks/Cells/TaskCampaignCell';
import TaskAssigneeCell from 'pages/allTasks/Cells/TaskAssigneeCell';
import { type AutopilotTaskDto, useAssignTaskGroupToUser } from '@urbanx/agx.activities.client';
import TaskStatusCell from 'pages/allTasks/Cells/TaskStatusCell';
import TaskDueCell from 'pages/allTasks/Cells/TaskDueCell';
import { useNavigateToTask } from 'utils/tasks/useNavigateToTask';
import { AgencyModel } from '@urbanx/agx.agencies.client';
import { useNavigate } from 'react-router-dom';
import { Pages } from 'constants/menu';
import { createTodaysTasksFilterState } from './createTodaysTasksFilterState';
import './TodaysTasks.scss';
import { useWorkflowConcurrencyError } from 'hooks/useWorkflowConcurrencyError';
import { getTaskDisplayGroups } from 'utils/tasks';

interface TodaysTasksProps {
  isLoading?: boolean;
  filtersPending?: boolean;
  todaysTasksCount?: number;
  assignedToUserIds?: string[];
  selectedAgencies?: string[];
  assigneeOptions: SelectChipOption[];
  assigneeOptionsPending?: boolean;
  agencies: AgencyModel[];
  isAgencyOptionsPending?: boolean;
  hasNoMatchingAgencies?: boolean;
}

const tableColumns: (string | IColumnConfig)[] = [
  'Task',
  'Agency',
  'Campaign',
  {
    name: 'Assignee',
    align: 'center',
    onClick: (e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>) => e.stopPropagation(),
  },
  'Status',
  { name: 'Due', align: 'right' },
];

const TodaysTasks = ({
  isLoading,
  filtersPending,
  todaysTasksCount,
  assignedToUserIds,
  selectedAgencies,
  assigneeOptions,
  assigneeOptionsPending,
  agencies,
  isAgencyOptionsPending,
  hasNoMatchingAgencies,
}: TodaysTasksProps) => {
  const assignmentWorkflowRef = useRef<AutopilotTaskDto | undefined>(undefined);
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError();
  const { tasks, searchAllTasksPending, refreshSearchInBackground } = useTaskSearch(
    createTodaysTasksFilterState(assignedToUserIds, selectedAgencies),
    filtersPending,
    hasNoMatchingAgencies
  );
  const { assignTaskGroupToUser, assignTaskGroupToUserPending } = useAssignTaskGroupToUser({
    onSuccess: () => {
      void refreshSearchInBackground();
    },
    onError: (error) => {
      if (!assignmentWorkflowRef.current) return;

      void handleWorkflowConcurrencyError(error, assignmentWorkflowRef.current, {
        refresh: refreshSearchInBackground,
      });
    },
  });
  const taskGroups = useMemo(() => getTaskDisplayGroups(tasks), [tasks]);
  const displayTasks = useMemo(() => taskGroups.map((group) => group.displayTask), [taskGroups]);

  const onAssignTaskToUser = useCallback(
    async (userId?: string, taskId?: string) => {
      if (!userId || !taskId || assignTaskGroupToUserPending) return;

      const taskGroup = taskGroups.find((group) => group.displayTask.id === taskId);
      if (!taskGroup) return;

      assignmentWorkflowRef.current = taskGroup.displayTask;

      await assignTaskGroupToUser({ taskId, userId });
    },
    [assignTaskGroupToUserPending, assignTaskGroupToUser, taskGroups]
  );

  const anythingPending = useMemo(() => {
    return searchAllTasksPending || isLoading || isAgencyOptionsPending || assigneeOptionsPending;
  }, [searchAllTasksPending, isLoading, isAgencyOptionsPending, assigneeOptionsPending]);

  const rows = useMemo(() => {
    if (anythingPending) return [];

    return displayTasks.map((task, idx) => {
      const isNew = !!task.isNew;

      return {
        Task: <TaskCell task={task} index={idx} isNew={isNew} />,
        Agency: <TaskAgencyCell task={task} agencies={agencies ?? []} index={idx} isNew={isNew} />,
        Campaign: (
          <TaskCampaignCell
            propertyAddress={task.propertyAddress ?? 'Unknown Address'}
            index={idx}
            isNew={isNew}
          />
        ),
        Assignee: (
          <TaskAssigneeCell
            task={task}
            assigneeOptions={assigneeOptions}
            onAssignTask={onAssignTaskToUser}
          />
        ),
        Status: <TaskStatusCell task={task} />,
        Due: <TaskDueCell task={task} />,
      };
    });
  }, [agencies, anythingPending, assigneeOptions, onAssignTaskToUser, displayTasks]);

  const navigateToTask = useNavigateToTask();

  const onTaskRowClicked = useCallback(
    (rowIndex: number) => {
      if (rowIndex < 0 || rowIndex >= displayTasks.length) return;

      const task = displayTasks[rowIndex];

      navigateToTask(task);
    },
    [displayTasks, navigateToTask]
  );

  const navigate = useNavigate();

  const onSeeAllTasksClick = useCallback(() => {
    navigate(`/${Pages.ALL_TASKS}`);
  }, [navigate]);

  return (
    <AgxSpacer direction="vertical" fill gap="sm">
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
      >
        <AgxSpacer direction="horizontal" contentAlignment="center" gap="spacing-md">
          <AgxTypography id={'todays-tasks-title'} type={'body_ui_strong'} size={'medium'}>
            {"Today's Tasks"}
          </AgxTypography>
          <AgxBadge
            type="colourfill"
            size="sm"
            colour="grey"
            text={todaysTasksCount?.toString() ?? '--'}
          />
        </AgxSpacer>
        <AgxButton
          variant="linkBrand"
          size="small"
          rightIcon={<Icon_Chevron direction={'right'} width={14} height={14} />}
          id={'todays-tasks-view-all-button'}
          text={'See all tasks'}
          onClick={onSeeAllTasksClick}
        />
      </AgxSpacer>

      {tasks.length >= 0 || searchAllTasksPending ? (
        <div className="todaysTasksTable">
          <AgxTable
            variant="default"
            tableStyle="rounded"
            columns={tableColumns}
            rows={rows}
            showLoadingState={anythingPending}
            onRowClick={onTaskRowClicked}
            noDataGlyphType="checklistSmile"
            noDataLabel="No task found."
            bodyBg="var(--bg-secondary)"
          />
        </div>
      ) : null}
      {tasks.length === 0 && !searchAllTasksPending && (
        <AgxEmptyState label="No tasks found" variant="minimal" />
      )}
    </AgxSpacer>
  );
};

export default TodaysTasks;
